import { useEffect, useRef, type RefObject } from "react"

const IMAGE_EXT: Record<string, string> = { "image/png": "png", "image/jpeg": "jpg" }
const DOC_TYPES = new Set([
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
])
// What a clipboard image is called when nobody named it.
const GENERIC_NAME = /^image\.(png|jpe?g)$/i

/** The files a paste carries that the simulators accept; empty for a text-only paste. */
function pastedFiles(data: DataTransfer | null): File[] {
  if (!data) return []
  const fromItems = Array.from(data.items ?? [])
    .filter((i) => i.kind === "file")
    .map((i) => i.getAsFile())
    .filter((f): f is File => !!f)
  const list = fromItems.length ? fromItems : Array.from(data.files ?? [])
  return list.filter((f) => f.type in IMAGE_EXT || DOC_TYPES.has(f.type))
}

type Entry = { enabled: () => boolean; deliver: (files: File[]) => void }
// Paste targets in mount order; only the most recently mounted enabled one takes a paste.
const entries: Entry[] = []

const isTextField = (t: EventTarget | null) =>
  t instanceof HTMLElement && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable || t.getAttribute("contenteditable") === "true")

const onDocPaste = (e: ClipboardEvent) => {
  if (isTextField(e.target)) return
  const picked = pastedFiles(e.clipboardData)
  if (!picked.length) return
  for (let i = entries.length - 1; i >= 0; i--) {
    if (!entries[i].enabled()) continue
    e.preventDefault()
    entries[i].deliver(picked)
    return
  }
}

/**
 * Hands the files of a paste anywhere on the page to `onFiles` while `target` is mounted and `enabled` (no click
 * needed first). With several targets mounted, the most recently mounted enabled one takes it. A paste with no
 * accepted files, or into a text field, is left alone, so text pastes work as before. An unnamed pasted image
 * becomes "Pasted image N.png" (or .jpg), N counting the images pasted through this hook.
 */
export function usePasteFiles(target: RefObject<HTMLElement | null>, onFiles: (files: File[]) => void, enabled = true): void {
  const latest = useRef(onFiles)
  const on = useRef(enabled)
  const count = useRef(0)
  useEffect(() => { latest.current = onFiles; on.current = enabled })

  useEffect(() => {
    const entry: Entry = {
      enabled: () => on.current,
      deliver: (picked) => latest.current(picked.map((f) => {
        const ext = IMAGE_EXT[f.type]
        if (!ext || (f.name && !GENERIC_NAME.test(f.name))) return f
        count.current += 1
        return new File([f], `Pasted image ${count.current}.${ext}`, { type: f.type })
      })),
    }
    if (!entries.length) document.addEventListener("paste", onDocPaste)
    entries.push(entry)
    return () => {
      entries.splice(entries.indexOf(entry), 1)
      if (!entries.length) document.removeEventListener("paste", onDocPaste)
    }
  }, [target])
}
